import type { FinancialSectionV2, Phone, ResidenceSectionV2 } from "@credit-acceptance/step6-bff-client";

import { splitDeferredCashDown } from "../../Financial/Financial.mapping";
import { asRecord, readField, readNumber, readYesNo } from "./callScript.mapping";
import {
  EMPTY_RESIDENCE_VERIFIED_FIELDS,
  type ResidenceCallScriptAccessory,
  type ResidenceCallScriptEntry,
  type ResidenceCallScriptHistory,
  type ResidenceCallScriptHistoryAnswer,
  type ResidenceVerifiedFields,
} from "./callScriptResidence.types";

// ---------------------------------------------------------------------------
// callScriptResidence.mapping
//
// Maps the dashboard onto the Customer Verification Call Script's "Saved
// Information" column. Mirrors callScriptEmploymentMapping.ts (the employment
// equivalent): ResidenceSectionV2 feeds the Contact, Physical/Mailing Address
// and Landlord blocks; FinancialSectionV2 feeds the Deal/down-payment block and
// the Vehicle Accessories table. whoIsPrimaryDriver has no saved counterpart.
// ---------------------------------------------------------------------------

/**
 * The applicant's phones that recap still treats as live, in dashboard order —
 * the order recap zips `phones.rows[]` against.
 * @param {Phone[] | undefined} phones - The dashboard's phone list.
 * @returns {Phone[]} Non-soft-deleted phones.
 */
export function selectLivePhones(phones: Phone[] | undefined): Phone[] {
  return (phones ?? []).filter((phone) => !phone.isSoftDeleted);
}

/**
 * Orders phones by `displayOrder`, so index 0 is the Primary Number and 1 the Secondary.
 * @param {Phone[]} phones - Live phones.
 * @returns {Phone[]} A sorted copy.
 */
export function rankPhones(phones: Phone[]): Phone[] {
  return [...phones].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

/**
 * Maps a saved `ResidenceSectionV2` into the modal's field shape, for
 * rendering the read-only "Saved Information" controls (same input types as
 * "Verified Information", disabled). The Deal/down-payment block comes from
 * the financial section instead — see mapFinancialToSavedDealFields.
 * @param {ResidenceSectionV2 | undefined} residence - The applicant's saved residence section, if any.
 * @returns {ResidenceVerifiedFields} The saved-fields state.
 */
export function mapResidenceToSavedFields(residence: ResidenceSectionV2 | undefined): ResidenceVerifiedFields {
  if (!residence) {
    return { ...EMPTY_RESIDENCE_VERIFIED_FIELDS };
  }
  const physical = residence.current;
  const mailing = residence.mailing;
  const phones = rankPhones(selectLivePhones(residence.phones));
  const landlord = physical?.landlords;
  // Same fallback as the Residence tab (residenceMapping.ts).
  const residenceOwnerTypeId = physical?.residenceOwnerTypeId ?? landlord?.residenceOwnerTypeId;

  return {
    ...EMPTY_RESIDENCE_VERIFIED_FIELDS,
    dateOfBirth: residence.dateOfBirth ?? "",
    primaryNumber: phones[0]?.number ?? "",
    secondaryNumber: phones[1]?.number ?? "",
    physicalStreetNumber: physical?.streetNumber ?? "",
    physicalStreetName: physical?.streetName ?? "",
    physicalApartmentNumber: physical?.apartmentNumber ?? "",
    physicalZip: physical?.zip ?? "",
    physicalCity: physical?.city ?? "",
    physicalState: physical?.state ?? "",
    mailingStreetNumber: mailing?.streetNumber ?? "",
    mailingStreetName: mailing?.streetName ?? "",
    mailingApartmentNumber: mailing?.apartmentNumber ?? "",
    mailingZip: mailing?.zip ?? "",
    mailingCity: mailing?.city ?? "",
    mailingState: mailing?.state ?? "",
    residenceOwnerType: residenceOwnerTypeId ? String(residenceOwnerTypeId) : "",
    landlordFirstName: landlord?.firstName ?? "",
    landlordLastName: landlord?.lastName ?? "",
    landlordCompanyName: landlord?.companyName ?? "",
    landlordPhone: landlord?.phoneNumber ?? "",
    landlordStreetNumber: landlord?.streetNumber ?? "",
    landlordStreetName: landlord?.streetName ?? "",
    landlordApartmentNumber: landlord?.apartmentNumber ?? "",
    landlordZip: landlord?.zip ?? "",
    landlordCity: landlord?.city ?? "",
    landlordState: landlord?.state ?? "",
  };
}

type SavedDealFields = Pick<
  ResidenceVerifiedFields,
  | "tradeIn"
  | "grossTradeInAmount"
  | "tradeInPayoffAmount"
  | "cashDownPaidOnDelivery"
  | "amountDueAfterDelivery"
  | "whenIsFinalAmountDue"
>;

/**
 * Maps the dashboard's financial section onto the Deal/down-payment rows'
 * "Saved Information" (CAPS2 buildScreenForDeferredDown). Absent figures stay
 * blank rather than 0 so an entered $0 still reads as a change.
 * @param {FinancialSectionV2 | undefined} financial - The deal's financial section, if any.
 * @returns {SavedDealFields} The saved Deal/down-payment values.
 */
export function mapFinancialToSavedDealFields(financial: FinancialSectionV2 | undefined): SavedDealFields {
  const downPayment = financial?.itemization?.downPayment;
  const { todayAmount, deferredTotal, finalDueDate } = splitDeferredCashDown(
    downPayment?.cashDown,
    financial?.deferredDownPayment?.entries ?? []
  );
  const grossTradeIn = downPayment?.grossTradeIn;
  const payoff = downPayment?.tradeInPayoff;
  let tradeIn = "";
  if (downPayment) {
    tradeIn = (grossTradeIn ?? 0) > 0 ? "Y" : "N";
  }
  return {
    tradeIn,
    grossTradeInAmount: grossTradeIn ?? "",
    // The stored payoff's sign isn't reliable (see Financial.mapping's mapItemization).
    tradeInPayoffAmount: payoff === undefined ? "" : Math.abs(payoff),
    cashDownPaidOnDelivery: todayAmount ?? "",
    amountDueAfterDelivery: downPayment ? deferredTotal : "",
    whenIsFinalAmountDue: finalDueDate?.slice(0, 10) ?? "",
  };
}

/**
 * Maps the deal's accessories into the Vehicle Accessories table rows.
 * @param {FinancialSectionV2 | undefined} financial - The deal's financial section, if any.
 * @returns {ResidenceCallScriptAccessory[]} One row per accessory, in dashboard order.
 */
export function mapAccessories(financial: FinancialSectionV2 | undefined): ResidenceCallScriptAccessory[] {
  return (financial?.accessories?.details ?? []).map((accessory, index) => ({
    id: String(index),
    description: accessory.description ?? accessory.name ?? "",
    price: accessory.customerPrice,
  }));
}

/**
 * Maps a raw residence history `answers` entry defensively, degrading
 * unknown/missing keys rather than throwing.
 * @param {unknown} answer - One raw answer record.
 * @returns {ResidenceCallScriptHistoryAnswer} The mapped answer.
 */
function mapResidenceHistoryAnswer(answer: unknown): ResidenceCallScriptHistoryAnswer {
  const row = asRecord(answer);
  return {
    verificationAnswerPk: readNumber(row, "verificationAnswerPk") ?? 0,
    verificationQuestionPk: readNumber(row, "verificationQuestionPk"),
    questionText: readField(row, "questionText"),
    answerEntered: readField(row, "answerEntered"),
    answerStored: readField(row, "answerStored"),
    isAnswerMatch: readYesNo(row, "isAnswerMatch"),
  };
}

/**
 * Maps a single raw residence history entry (recap's call record, forwarded
 * verbatim by `getResidenceHistory`) to the app's view model.
 * @param {unknown} entry - One raw history record.
 * @returns {ResidenceCallScriptEntry} The mapped entry.
 */
export function mapResidenceCallScriptEntry(entry: unknown): ResidenceCallScriptEntry {
  const row = asRecord(entry);
  return {
    verificationPhoneCallPk: readNumber(row, "verificationPhoneCallPk") ?? 0,
    verificationOutcome: readField(row, "verificationOutcome"),
    phoneNumberDialed: readField(row, "phoneNumberDialed"),
    isPhoneNoBad: readYesNo(row, "isPhoneNoBad"),
    isVerificationOutsourced: readYesNo(row, "isVerificationOutsourced"),
    dob: readField(row, "dob"),
    occurredAt: readField(row, "occurredAt"),
    analystName: readField(row, "analystName"),
    applicantAddressPk: readNumber(row, "applicantAddressPk"),
    answers: Array.isArray(row.answers) ? row.answers.map((answer) => mapResidenceHistoryAnswer(answer)) : [],
  };
}

/**
 * Maps a residence Call Script history response into entries. recap returns
 * a bare array newest-first; the generated client types it `void`, so — like
 * the employment `mapCallScriptHistory` — an object wrapping the array under
 * `entries` / `history` / `rows` is also accepted, anything else yields none.
 * @param {unknown} payload - The raw `getResidenceHistory` response.
 * @returns {ResidenceCallScriptHistory} The history view model.
 */
export function mapResidenceCallScriptHistory(payload: unknown): ResidenceCallScriptHistory {
  const record = asRecord(payload);
  const candidate = Array.isArray(payload) ? payload : (record.entries ?? record.history ?? record.rows);
  const entries = Array.isArray(candidate) ? candidate : [];
  return { entries: entries.map((entry) => mapResidenceCallScriptEntry(entry)) };
}
